import type { SanityArticleSummary } from "@/lib/sanity.types";
import { Link } from "@tanstack/react-router";
import { Calendar, Clock, User } from "lucide-react";
import ShareDropdown from "./ShareDropdown";

type BlogCardProps = {
  post: SanityArticleSummary;
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogCard({ post }: BlogCardProps) {
  const href = `/article/${post.slug}`;
  const fullUrl = `https://zapify.pro/article/${post.slug}`;
  const imageUrl = post.cover?.url || "";
  const readingTime = post.readingTime && post.readingTime > 0 ? post.readingTime : 1;

  return (
    <article className="group relative rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-soft hover:border-ring/30">
      {/* Image — overflow-hidden only on image container */}
      <Link to={href}>
        <div className="relative aspect-video overflow-hidden rounded-t-xl bg-secondary">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={post.cover?.alternativeText || post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <span className="text-xs font-bold uppercase tracking-widest">No Image</span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-5 sm:p-6">
        {/* Category Badge + Share */}
        <div className="flex items-center justify-between mb-3">
          {post.category ? (
            <Link
              to="/blog"
              search={{ page: 1, category: post.category.slug }}
              className="inline-block rounded-full px-3 py-1 text-xs font-semibold gradient-text border border-ring/20 bg-secondary transition-colors hover:bg-accent"
            >
              {post.category.name}
            </Link>
          ) : (
            <span />
          )}
          <ShareDropdown url={fullUrl} title={post.title} compact />
        </div>

        {/* Title */}
        <Link to={href}>
          <h3 className="text-lg font-bold text-foreground leading-snug mb-2 transition-colors group-hover:text-primary line-clamp-2">
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
          {post.description}
        </p>

        {/* Meta: Date, Reading Time, Author */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{readingTime} min read</span>
            </span>
          </div>

          {post.author && (
            <Link
              to={`/blog/author/${post.author.slug || post.author.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-1.5 font-medium text-foreground/70 hover:text-primary transition-colors"
            >
              {post.author.avatarUrl ? (
                <img
                  src={post.author.avatarUrl}
                  alt={post.author.name}
                  className="w-5 h-5 rounded-full object-cover"
                />
              ) : (
                <User className="w-3.5 h-3.5" />
              )}
              {post.author.name}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
